import React, { useState, useEffect, useRef } from "react";
import Cookies from "js-cookie";
import "./OnboardingChat.css";

const INFERENCE_API = "https://aimegle.razorfart.com";


function OnboardingChat({ handleMatch }) {
  const [_, setMessages] = useState([]);
  const [currentMessage, setCurrentMessage] = useState("");
  const [messageQueue, setMessageQueue] = useState([
    "Welcome! Tell me a little more about you, and I'll create a summary. Your summary will be used for my matching engine!",
    "What are your favorite movies or TV shows?",
    "What are your favorite things to do in your free time?",
    "If you could go anywhere in the world right now, where would it be and why?",
    "What are you most passionate about?",
  ]);
  const [doneTyping, setDoneTyping] = useState(true); // Initially set to true to allow the first message
  const [answers, setAnswers] = useState([]);
  const [currentAnswer, setCurrentAnswer] = useState("");
  const inputRef = useRef(null);
  const [isMatching, setIsMatching] = useState(false);
  const [isReturningUser, setIsReturningUser] = useState(false);
  const [generatedSummary, setGeneratedSummary] = useState("");

  const toggleNextMessage = async (answers) => {
    if (doneTyping && messageQueue.length > 0) {
      setCurrentMessage("");
      const nextMessage = messageQueue.shift();
      setDoneTyping(false); // Start typing, so set to false
      simulateTyping(nextMessage);
    } else if (doneTyping && messageQueue.length === 0) {
      setIsMatching(true);
      try {
        const summarizeStream = await fetch(
          INFERENCE_API + "/inference/summarize-questionnaire/stream",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "X-API-KEY": "spencer1",
            },
            body: JSON.stringify({
              text: answers,
            }),
          }
        );
        let summary = '';
        // eslint-disable-next-line no-undef
        const reader = summarizeStream.body.pipeThrough(new TextDecoderStream()).getReader()
        try {
            while (true) {
                const {value, done} = await reader.read();
                if (done) break;
                if (value !== '<|eot_id|>') {
                    setGeneratedSummary((prev) => prev + value);
                    summary += value;
                }
            }
        } finally {
            reader.cancel();
        }

        console.log('SUMMARY', summary);
        const userId = generateUUID();
        Cookies.set("summary", summary, { expires: 7, path: "/" }); // Set 'summary' cookie with the summary
        Cookies.set("userId", userId, { expires: 7, path: "/" }); // Set 'userId' cookie with a random UUID

        await fetch(INFERENCE_API + "/summary/", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-API-KEY": "spencer1",
          },
          body: JSON.stringify({
            summaries: [summary],
            ids: [userId],
          }),
        });

        const match = await getMatch(userId, summary);
        if (match) {
          console.log("MATCHED ROOM", match);
          // Redirect to the room
          handleMatch(generateNick(), match);
        }
      } catch (error) {
        console.log("ERROR:", error);
      }
    }
  };

  const getMatch = async (userId, summary) => {
    try {
      const timeoutPromise = (timeout) =>
        new Promise((resolve, reject) =>
          setTimeout(() => reject(new Error("Request timed out")), timeout)
        );
      const fetchPromise = fetch(INFERENCE_API + "/matching/", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-API-KEY": "spencer1",
        },
        body: JSON.stringify({
          user_id: userId,
          summary: summary,
        }),
      });
      const response = await Promise.race([
        fetchPromise,
        timeoutPromise(70000),
      ]);

      if (response.status === 200) {
        const data = await response.json();
        if (data.matched) {
          console.log("MATCHED", data);
          const roomId = data.room_id;
          return roomId;
        }
      }
    } catch (error) {
      console.log("ERROR:", error);
      return getMatch(userId, summary); // Retry
    }
  };

  const simulateTyping = (message, index = 0) => {
    if (index < message.length) {
      setCurrentMessage((prev) => prev + message[index]);
      setTimeout(() => simulateTyping(message, index + 1), 30);
    } else {
      setMessages((prev) => [...prev, currentMessage]);
      setDoneTyping(true); // Done typing the current message
    }
  };

  const startMatching = async () => {
    setIsMatching(true);
    const match = await getMatch(Cookies.get("userId"), Cookies.get("summary"));
    if (match) {
      console.log("MATCHED ROOM", match);
      // Redirect to the room
      handleMatch(generateNick(), match);
    }
  };

  // Use useEffect to call toggleNextMessage on initial render
  useEffect(() => {
    if (Cookies.get("userId")) {
      setIsReturningUser(true);
      setGeneratedSummary(Cookies.get("summary"));
      return;
    }
    toggleNextMessage();
    inputRef.current.focus();
  }, []); // Empty array means this effect runs only once after the initial render

  return (
    <div className="onboardContainer">
      {isReturningUser && !isMatching && (
        <React.Fragment>
          <p className="onboardText">Welcome back!</p>
          <p className="onboardText">
            You have already completed the onboarding process.
          </p>
          <button
            className="btn"
            style={{ maxWidth: 300 }}
            onClick={startMatching}
          >
            Match Me
          </button>
        </React.Fragment>
      )}
      {!isMatching && !isReturningUser && (
        <React.Fragment>
          <p className="onboardText">{currentMessage}</p>
          <input
            className="onboardTextBox"
            type="text"
            ref={inputRef}
            value={currentAnswer}
            placeholder="Type a message..."
            onChange={(e) => {
              setCurrentAnswer(e.target.value);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                toggleNextMessage([...answers, {question: currentMessage, answer: currentAnswer}]);
                setAnswers((prev) => [...prev, {question: currentMessage, answer: currentAnswer}]);
                setCurrentAnswer("");
              }
            }}
          />
        </React.Fragment>
      )}
      {isMatching && <React.Fragment>
        <p className="onboardText">Please wait while we find a match for you.</p>
        {
            generatedSummary && (
                <React.Fragment>
                    <p className="onboardText">Your summary:</p>
                    <p className="onboardText">{generatedSummary}</p>
                </React.Fragment>)
        }
        </React.Fragment>}
    </div>
  );
}

function generateUUID() {
  return ([1e7] + -1e3 + -4e3 + -8e3 + -1e11).replace(/[018]/g, (c) =>
    (
      c ^
      (crypto.getRandomValues(new Uint8Array(1))[0] & (15 >> (c / 4)))
    ).toString(16)
  );
}

function generateNick() {
  const randomNumber = Math.floor(Math.random() * (999 - 100 + 1)) + 100;
  return `Anon${randomNumber}`;
}

export default OnboardingChat;



// WEBPACK FOOTER //
// ./src/components/OnboardingChat/index.js